import { useEffect, useContext, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  MessageSquareText,
  Search,
  Plus,
  Save,
  RefreshCcw,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronUp,
  Hash,
  Type,
  ArrowLeft,
  Calendar,
} from "lucide-react";

const BASE_URL = "http://nissi-india.com"; // change if needed

const EmployeeRemarks = () => {
  const { employeeId } = useParams(); // /payroll/EmployeeRemarks/:employeeId
  const navigate = useNavigate();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const branchId = localStorage.getItem("branchId") || ""; // ASP: Session["branchid"]

  // form
  const [showForm, setShowForm] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [title, setTitle] = useState("");
  const [details, setDetails] = useState("");

  // list
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // pagination (client-side)
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);

  const screenBadge = useMemo(() => "INFRA SCREEN : EMP REMARKS", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const dangerBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ` +
    (selectedIds.length === 0 ? "bg-gray-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700");

  const resetForm = () => {
    setEditingId(null);
    setTitle("");
    setDetails("");
  };

  const fetchList = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!String(employeeId).trim()) {
      toast.error("employeeId missing in URL");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/payroll/remarks`, {
        params: {
          finYearId: Number(finYearId),
          employeeId: Number(employeeId),
        },
      });

      if (res?.data?.success) {
        const list = res.data.rows || [];

        // client-side search (Title/Details/EmpCode/EmpName/Date)
        const s = search.trim().toLowerCase();
        const filtered =
          s.length === 0
            ? list
            : list.filter((r) => {
                const hay = [
                  r.fld_Employeecode,
                  r.fld_Employee,
                  r.RemarkDate,
                  r.fld_Title,
                  r.fld_Details,
                ]
                  .filter(Boolean)
                  .join(" ")
                  .toLowerCase();
                return hay.includes(s);
              });

        setRows(filtered);

        // reset selection on new fetch
        setSelectedIds([]);
        setSelectAll(false);

        // reset paging if page exceeds
        setPage(1);
      } else {
        toast.error(res?.data?.message || "Failed to load remarks.");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Something went wrong.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    await fetchList();
  };

  const handleAddNew = () => {
    resetForm();
    setShowForm(true);
    toast.info("Form ready for new remark");
  };

  const handleEdit = async (remarkId) => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/payroll/remarks/detail/${remarkId}`, {
        params: { finYearId: Number(finYearId) },
      });

      if (res?.data?.success && res?.data?.row) {
        setEditingId(Number(res.data.row.fld_Remark_ID));
        setTitle(res.data.row.fld_Title || "");
        setDetails(res.data.row.fld_Details || "");
        setShowForm(true);
        toast.info("Loaded for editing");
      } else {
        toast.error(res?.data?.message || "Record not found.");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Something went wrong.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();

    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!String(employeeId).trim()) {
      toast.error("employeeId missing in URL");
      return;
    }

    // ASP client validation messages
    if (!title.trim()) {
      toast.warn("Please Enter Title..");
      return;
    }
    if (!details.trim()) {
      toast.warn("Please Enter Details..");
      return;
    }

    try {
      setLoading(true);

      if (editingId) {
        const res = await axios.put(
          `${BASE_URL}/api/payroll/remarks/${editingId}`,
          { title: title.trim(), details: details.trim() },
          { params: { finYearId: Number(finYearId) } }
        );

        if (res?.data?.success) {
          toast.success(res?.data?.message || "Updated Successfully..");
          resetForm();
          await fetchList();
        } else {
          toast.error(res?.data?.message || "Plz Retry..");
        }
      } else {
        if (!String(branchId).trim()) {
          toast.error("branchid missing (required for insert). Set localStorage.branchid like ASP Session['branchid'].");
          return;
        }

        const res = await axios.post(
          `${BASE_URL}/api/payroll/remarks`,
          {
            branchId: Number(branchId),
            employeeId: Number(employeeId),
            title: title.trim(),
            details: details.trim(),
          },
          { params: { finYearId: Number(finYearId) } }
        );

        if (res?.data?.success) {
          toast.success(res?.data?.message || "Inserted Successfully..");
          resetForm();
          await fetchList();
        } else {
          toast.error(res?.data?.message || "Plz Retry..");
        }
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Something went wrong.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
      setSelectAll(false);
      return;
    }
    setSelectedIds(pagedRows.map((r) => Number(r.fld_Remark_ID)));
    setSelectAll(true);
  };

  const handleDeleteSelected = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }

    if (selectedIds.length === 0) {
      toast.warn("Please select any one");
      return;
    }

    const ok = window.confirm("Are you sure to delete?");
    if (!ok) return;

    try {
      setLoading(true);

      const res = await axios.delete(`${BASE_URL}/api/payroll/remarks`, {
        params: { finYearId: Number(finYearId) },
        data: { remarkIds: selectedIds },
      });

      if (res?.data?.success) {
        toast.success(res?.data?.message || "Deleted Successfully");
      } else {
        toast.error(res?.data?.message || "Delete failed");
      }

      await fetchList();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Something went wrong.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // client-side paging on filtered rows
  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;
  const pagedRows = rows.slice(start, start + pageSize);

  const showingFrom = total === 0 ? 0 : start + 1;
  const showingTo = Math.min(start + pageSize, total);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">EMPLOYEE REMARKS</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                <MessageSquareText className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Employee Remark Management
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Add / Edit / Delete / Search / Pagination
                </div>
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {screenBadge}
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"}`}>
                {total} Records
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-purple-900/40 text-purple-100" : "bg-purple-100 text-purple-800"}`}>
                Employee ID: {employeeId}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={() => navigate(-1)} className={ghostBtn} disabled={loading}>
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>

              <button onClick={() => fetchList()} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button onClick={handleAddNew} className={primaryBtn}>
                <Plus className="w-4 h-4" />
                Add New
              </button>

              <button onClick={() => setShowForm((v) => !v)} className={ghostBtn}>
                {showForm ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showForm ? "Hide Form" : "Show Form"}
              </button>
            </div>
          </div>

          {/* Search */}
          <form onSubmit={handleSearch} className={cardBase + " p-5 mb-6"}>
            <div className="flex flex-col md:flex-row md:items-end gap-4">
              <div className="flex-1">
                <label className={labelBase}>Search (Emp Code / Name / Date / Title / Details)</label>
                <div className="mt-2 relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Search className="w-4 h-4 text-gray-400" />
                  </div>
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Type to search..."
                    className={`pl-11 ${inputBase}`}
                  />
                </div>
              </div>

              <button type="submit" className={primaryBtn} disabled={loading}>
                <Search className="w-4 h-4" />
                Search
              </button>
            </div>
          </form>

          {/* Collapsible Form */}
          {showForm && (
            <form onSubmit={handleSave} className={cardBase + " p-6 mb-6"}>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
                <div className="text-lg font-extrabold">
                  {editingId ? `Edit Remark (ID: ${editingId})` : "New Remark"}
                </div>
                <div className="flex gap-3">
                  <button type="button" onClick={resetForm} className={ghostBtn}>
                    <RefreshCcw className="w-4 h-4" />
                    Clear
                  </button>
                  <button type="submit" className={primaryBtn} disabled={loading}>
                    <Save className="w-4 h-4" />
                    {editingId ? "Update" : "Save"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelBase}>
                    Title <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-2 flex items-center gap-2">
                    <Type className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <input
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Enter title"
                      className={inputBase}
                    />
                  </div>
                </div>

                <div>
                  <label className={labelBase}>Remark Date</label>
                  <div className="mt-2 flex items-center gap-2">
                    <Calendar className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <input value={"Auto (Now)"} readOnly className={inputBase + " opacity-80"} />
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className={labelBase}>
                    Details <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-2 flex items-start gap-2">
                    <Hash className={`w-5 h-5 mt-3 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <textarea
                      value={details}
                      onChange={(e) => setDetails(e.target.value)}
                      placeholder="Enter details..."
                      className={inputBase}
                      rows={4}
                    />
                  </div>
                </div>
              </div>
            </form>
          )}

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold">S.No</th>
                    <th className="px-4 py-4 text-center font-bold">Emp Code</th>
                    <th className="px-4 py-4 text-center font-bold">Emp Name</th>
                    <th className="px-4 py-4 text-center font-bold">Date</th>
                    <th className="px-4 py-4 text-center font-bold">Title</th>
                    <th className="px-4 py-4 text-center font-bold">Remarks</th>
                    <th className="px-4 py-4 text-center font-bold">Actions</th>
                    <th className="px-4 py-4 text-center font-bold">
                      <div className="flex items-center justify-center gap-2">
                        Select All
                        <input type="checkbox" checked={selectAll} onChange={handleSelectAll} />
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 8 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : pagedRows.length === 0 ? (
                    <tr>
                      <td colSpan={8} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    pagedRows.map((r, idx) => {
                      const rid = Number(r.fld_Remark_ID);
                      return (
                        <tr
                          key={rid}
                          className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}
                        >
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {(safePage - 1) * pageSize + idx + 1}
                          </td>

                          {/* Employee code acts like ASP LinkButton View */}
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            <button
                              type="button"
                              onClick={() => handleEdit(rid)}
                              className="font-bold underline text-blue-600 hover:text-blue-800"
                              title="View / Edit"
                            >
                              {r.fld_Employeecode || "-"}
                            </button>
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_Employee || "-"}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.RemarkDate || "-"}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_Title || "-"}
                          </td>

                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            <div className="max-w-xl whitespace-pre-wrap break-words">
                              {r.fld_Details || "-"}
                            </div>
                          </td>

                          <td className="px-4 py-4 text-center">
                            <button
                              type="button"
                              onClick={() => handleEdit(rid)}
                              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-bold rounded-2xl hover:bg-blue-700 transform hover:-translate-y-0.5 transition-all shadow"
                            >
                              <Pencil className="w-4 h-4" />
                              Edit
                            </button>
                          </td>

                          <td className="px-4 py-4 text-center">
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(rid)}
                              onChange={() => toggleSelect(rid)}
                              className="w-4 h-4"
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bottom row: pagination + delete */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {totalPages > 1 && (
                <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                    className={`px-4 py-2 text-sm font-bold ${
                      safePage === 1
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    Prev
                  </button>

                  {Array.from({ length: totalPages }).map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setPage(i + 1)}
                      className={`px-4 py-2 text-sm font-bold transition ${
                        safePage === i + 1
                          ? `${primaryBtnClass} text-white`
                          : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}

                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={safePage === totalPages}
                    className={`px-4 py-2 text-sm font-bold ${
                      safePage === totalPages
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    Next
                  </button>
                </div>
              )}

              <button onClick={handleDeleteSelected} disabled={selectedIds.length === 0} className={dangerBtn}>
                <Trash2 className="w-4 h-4" />
                Delete Selected
              </button>
            </div>
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default EmployeeRemarks;
